import { FC, useEffect, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { replaceDate } from "@/helper/dateHandling";
import { api } from "@/helper/api";
import RestrictedPage from "@/consumer/restrictedPage";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { priceInputRegex } from "@/helper/regex";
import { trackedServiceTemplateEntityAdapter } from "@/components/trackedService/trackedServiceTemplateEntityAdapter";

const TrackedServiceTemplatesTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("trackedservice_template_tableHeading"));
  const [trackedServiceTypeForFiltering, setTrackedServiceTypeForFiltering] =
    useState<any[]>([]);
  const [mainCategoryOptionsForFiltering, setMaincategoryOptionsForFiltering] =
    useState<any[]>([]);

  useEffect(() => {
    getTrackedServiceType(setTrackedServiceTypeForFiltering);
    getMainCategories(setMaincategoryOptionsForFiltering);
  }, []);
  const trackedServiceTemplateColumns = [
    { header: t("id"), accessorKey: "id", id: "id" },
    {
      header: t("internal_name"),
      accessorKey: "internal_name",
      id: "internal_name",
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
    },
    {
      header: t("description"),
      accessorKey: "description",
      id: "description",
      enableForCreate: true,
      textarea: true,
    },
    {
      header: t("estimated_duration"),
      accessorKey: "estimated_duration",
      id: "estimated_duration",
      enableForCreate: true,
      textfieldType: "number",
      required: true,
      errorMessage: t("field_required"),
    },
    {
      header: t("price_with_currency", { currencySymbol: "€" }),
      accessorKey: "price",
      filterVariant: "price",
      customColumnFilterId: "price",
      customFilterParameter: "__icontains",
      inputMode: "decimal",
      rule: priceInputRegex,
      blockInvalidInput: true,
      errorMessageValidation: t("invalid_characters"),
      id: "price",
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()} €</span>;
      },
    },
    {
      header: t("vat_short"),
      id: "vat",
      accessorKey: "vat",
      textfieldType: "number",
      allowZeroAsValue: true,
      enableForCreate: true,
      Cell: ({ cell }: any) => {
        const value = `${cell.getValue()}%`;

        return <span>{value}</span>;
      },
    },
    {
      header: t("relevant_for_timesheet"),
      id: "relevant_for_timesheet",
      accessorKey: "relevant_for_timesheet",
      enableForCreate: true,
      required: true,
      errorMessage: t("field_required"),
      filterVariant: "select",
      customColumnFilterId: "relevant_for_timesheet",
      setNoFilterParameter: true,
      selectOptions: [
        { value: true, label: t("yes") },
        { value: false, label: t("no") },
      ],
      filterSelectOptions: [
        { value: "true", label: t("yes") },
        { value: "false", label: t("no") },
      ],
      accessorFn: (row: any) => {
        if (row.relevant_for_timesheet) {
          return t("yes");
        } else return t("no");
      },
    },
    {
      header: t("trackedservice_type"),
      accessorKey: "tracked_service_type",
      id: "tracked_service_type",
      enableForCreate: true,
      entity: "trackedServiceType",
      filterVariant: "select",
      filterSelectOptions: trackedServiceTypeForFiltering,
      required: true,
      errorMessage: t("field_required"),
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue().name}</span>;
      },
    },
    {
      header: t("category"),
      accessorKey: "category",
      id: "category",
      enableForCreate: true,
      entity: "mainCategory",
      filterVariant: "select",
      filterSelectOptions: mainCategoryOptionsForFiltering,
      required: true,
      errorMessage: t("field_required"),
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue().name}</span>;
      },
    },
    {
      header: t("created_on"),
      accessorKey: "created_on",
      id: "created_on",
      filterVariant: "date",
      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("created_by"),
      accessorKey: "created_by",
      customColumnFilterId: "created_by_name",

      id: "created_by",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
    {
      header: t("modified_on"),
      accessorKey: "modified_on",
      filterVariant: "date",
      id: "modified_on",
      sortingFn: "datetime",
      Cell: ({ cell }: any) => {
        return <span>{replaceDate(cell.getValue())}</span>;
      },
    },
    {
      header: t("modified_by"),
      accessorKey: "modified_by",
      customColumnFilterId: "modified_by_name",
      id: "modified_by",
      Cell: ({ cell }: any) => {
        return <span>{cell.getValue()?.name}</span>;
      },
    },
  ];
  return (
    <RestrictedPage permissionRequired="frontend_view_trackedservice_tracked_service_templates">
      <GenericCrudTable
        entity="trackedServiceTemplate"
        entityAdapter={trackedServiceTemplateEntityAdapter}
        columns={trackedServiceTemplateColumns}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
      />
    </RestrictedPage>
  );
};

export default TrackedServiceTemplatesTable;

function getTrackedServiceType(setOptions: any) {
  api.genericApiRequest({
    method: "get",
    entity: "trackedServiceType",
    successHandler: (res: any) => {
      const typeOptions = res.data.results.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setOptions(typeOptions);
    },
  });
}
function getMainCategories(setOptions: any) {
  api.genericApiRequest({
    method: "get",
    entity: "mainCategory",
    successHandler: (res: any) => {
      const categoryOptions = res.data.results.map((item: any) => ({
        value: item.id,
        label: item.name,
      }));
      setOptions(categoryOptions);
    },
  });
}
